
"use client";

import Image from "next/image";
import Link from "next/link";
import { useState, useEffect } from "react";
import mentorsData from "@/data/mentors.json"; // Import the mentors data

export default function MentorshipCard({ program, isVertical = false }) {
  const [isDesktop, setIsDesktop] = useState(false);

  // Check if screen is desktop size
  useEffect(() => {
    const checkScreenSize = () => {
      setIsDesktop(window.innerWidth >= 1024); // lg breakpoint
    };

    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);

    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  // Find mentor data for this program
  const programMentors = mentorsData.find((item) => item.slug === program.slug);

  // Extract mentor information
  const mentors = [];
  if (programMentors) {
    for (let i = 1; i <= 11; i++) {
      const nameKey = `mentor${i}name`;
      const imageKey = `mentor${i}image`;
      const postKey = `mentor${i}post`;

      if (
        programMentors[nameKey] &&
        programMentors[imageKey] &&
        programMentors[postKey]
      ) {
        mentors.push({
          name: programMentors[nameKey],
          image: programMentors[imageKey],
          position: programMentors[postKey],
        });
      }
    }
  }

  // Number of mentors to display in stack
  const visibleCount = 3;

  // Calculate additional mentors beyond visible count
  const additionalCount = Math.max(0, mentors.length - visibleCount);

  // Generate text for mentor companies
  const generateMentorCompaniesText = () => {
    if (mentors.length === 0) {
      return program.designation || "Top Companies";
    }

    // Extract company names from positions (assuming format "Position, Company")
    const companies = mentors.map((mentor) => {
      const parts = mentor.position.split(",");
      if (parts.length > 1) {
        return parts[1].trim().split(" ")[0]; // Get first word after comma
      }
      // If no comma, try to extract last word as it's often the company
      const words = mentor.position.split(" ");
      return words[words.length - 1];
    });

    // Limit to first 2 companies + "& more" if there are more
    if (companies.length <= 2) {
      return companies.join(", ");
    } else {
      return `${companies[0]}, ${companies[1]} & more`;
    }
  };

  // Desktop Vertical Layout (New)
  if (isVertical && isDesktop) {
    return (
      <Link href={`/${program.slug}`} className="block no-underline h-full">
        <div className="bg-white p-6 rounded-3xl overflow-hidden border h-full flex flex-col">
          {/* Image Area */}
          <div className="relative w-full h-[220px] overflow-hidden mb-4">
            <Image
              src={program.mainImage}
              alt={program.heading}
              fill
              className="object-cover rounded-2xl"
            
            />
          </div>

          {/* Content Area */}
          <div className=" flex flex-col flex-grow">
            <h3 className="text-[#002726] text-2xl font-medium mb-3 leading-tight">
              {program.heading}
            </h3>
            <p className="text-[#002726] mb-6 text-base font-normal leading-normal">
              {program.subheading}
            </p>

            {/* Program Details */}
            <div className="flex flex-wrap gap-x-6 gap-y-4 mb-4">
              <div className="flex items-center gap-2">
                <Image
                  src="https://d31bwppm8yl9g2.cloudfront.net/learner/website/weeks.svg"
                  width={17}
                  height={17}
                  className="w-[17px] h-[17px]"
                  style={{ objectFit: "contain" }}
                  priority
                  alt="Program Duration Logo"
                />
                <span className="text-[#002726] text-base font-normal">
                  {program.duration}
                </span>
              </div>

              <div className="flex items-center gap-2">
                <Image
                  src="https://d31bwppm8yl9g2.cloudfront.net/learner/website/sessions.svg"
                  width={17}
                  height={17}
                  className="w-[17px] h-[17px]"
                  style={{ objectFit: "contain" }}
                  priority
                  alt="Program Type Logo"
                />
                <span className="text-[#002726] text-base font-normal">
                  {program.type || "Live And Recorded"}
                </span>
              </div>
            </div>

            <div className="flex items-center gap-2 mb-6 ">
              <Image
                src="https://d31bwppm8yl9g2.cloudfront.net/learner/website/gsdate.svg"
                width={17}
                height={17}
                className="w-[17px] h-[17px]"
                style={{ objectFit: "contain" }}
                priority
                alt="Program Date Logo"
              />
              <span className="text-[#002726] text-base font-normal">
                {program.date || program.status || "Coming Soon"}
              </span>
            </div>

            {/* Push mentor section to bottom */}
            <div className="mt-auto">
              {/* Mentor Details */}
              <div className="flex items-center gap-3">
                <div className="flex items-center">
                  {mentors.length > 0 ? (
                    <div className="flex">
                      {/* Display first 3 mentors in stack */}
                      {mentors.slice(0, visibleCount).map((mentor, index) => (
                        <div
                          key={index}
                          className={`relative w-10 h-10 rounded-full overflow-hidden border border-black ${
                            index > 0 ? "-ml-3" : ""
                          }`}
                        >
                          <Image
                            src={mentor.image}
                            alt={mentor.name}
                            fill
                            className="object-cover"
                          />
                        </div>
                      ))}

                      {/* Show +X more circle if there are additional mentors */}
                      {additionalCount > 0 && (
                        <div className="relative w-10 h-10 rounded-full overflow-hidden border-2 border-white -ml-3">
                          <div className="bg-[#012726] w-full h-full flex items-center justify-center text-white font-medium text-xs">
                            +{additionalCount}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    // Fallback to legacy format if no mentors found
                    <div className="flex">
                      <div className="relative w-10 h-10 rounded-full overflow-hidden border border-black">
                        <Image
                          src={program.mentorImage || "/placeholder-mentor.jpg"}
                          alt="Mentor"
                          fill
                          className="object-cover"
                        />
                      </div>
                      <div className="relative w-10 h-10 rounded-full overflow-hidden border-2 border-white -ml-3">
                        <div className="bg-[#012726] w-full h-full flex items-center justify-center text-white font-medium text-xs">
                          +{program.plusCount || 1}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <div>
                  <p className="text-[#002726] text-sm font-medium">
                    {program.mentorTitle || "Multiple mentors from"}
                  </p>
                  <p className="text-[#002726] text-sm">
                    {mentors.length > 0
                      ? generateMentorCompaniesText()
                      : program.mentorCompanies ||
                        program.designation ||
                        "Google, Meta and 2 more"}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Link>
    );
  }

  // Mobile/Tablet Layout (unchanged from original)
  if (!isDesktop || !isVertical) {
    return (
      <Link href={`/${program.slug}`} className="block no-underline h-full">
        <div className="bg-white rounded-3xl overflow-hidden border h-full flex flex-col p-6">
          {/* Main Image Area */}
          <div className="relative w-full h-[220px] overflow-hidden rounded-md">
            <Image
              src={program.mainImage}
              alt={program.heading}
              fill
              className="object-cover rounded-3xl"
            />
          </div>

          {/* Content Area */}
          <div className="py-4 flex flex-col flex-grow">
            <h3 className="text-[#002726] text-2xl font-semibold mb-3">
              {program.heading}
            </h3>
            <p className="text-[#002726] mb-6 text-base font-normal leading-normal">
              {program.subheading}
            </p>

            {/* Program Details */}
            <div className="mt-auto">
              <div className="flex flex-wrap gap-x-6 gap-y-4 mb-4">
                <div className="flex items-center gap-2">
                  <Image
                    src="https://d31bwppm8yl9g2.cloudfront.net/learner/website/weeks.svg"
                    width={17}
                    height={17}
                    className="md:w-[17px] md:h-auto w-[17px]"
                    style={{ objectFit: "contain" }}
                    priority
                    alt="Program Duration Logo"
                  />
                  <span className="text-[#002726] text-base font-normal">
                    {program.duration}
                  </span>
                </div>

                <div className="flex items-center gap-2">
                  <Image
                    src="https://d31bwppm8yl9g2.cloudfront.net/learner/website/sessions.svg"
                    width={17}
                    height={17}
                    className="md:w-[17px] md:h-auto w-[17px]"
                    style={{ objectFit: "contain" }}
                    priority
                    alt="Program Type Logo"
                  />
                  <span className="text-[#002726] text-base font-normal">
                    {program.type || "Live And Recorded"}
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-2 mb-6">
                <Image
                  src="https://d31bwppm8yl9g2.cloudfront.net/learner/website/gsdate.svg"
                  width={17}
                  height={17}
                  className="md:w-[17px] md:h-auto w-[17px]"
                  style={{ objectFit: "contain" }}
                  priority
                  alt="Program Date Logo"
                />
                <span className="text-[#002726] text-base font-normal">
                  {program.date || program.status || "Coming Soon"}
                </span>
              </div>

              {/* Mentor Details */}
              <div className="flex items-center gap-3">
                <div className="flex items-center">
                  {mentors.length > 0 ? (
                    <div className="flex">
                      {/* Display first 3 mentors in stack */}
                      {mentors.slice(0, visibleCount).map((mentor, index) => (
                        <div
                          key={index}
                          className={`relative w-10 h-10 rounded-full overflow-hidden border border-black ${
                            index > 0 ? "-ml-3" : ""
                          }`}
                        >
                          <Image
                            src={mentor.image}
                            alt={mentor.name}
                            fill
                            className="object-cover"
                          />
                        </div>
                      ))}

                      {/* Show +X more circle if there are additional mentors */}
                      {additionalCount > 0 && (
                        <div className="relative w-10 h-10 rounded-full overflow-hidden border-2 border-white -ml-3">
                          <div className="bg-[#012726] w-full h-full flex items-center justify-center text-white font-medium text-xs">
                            +{additionalCount}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    // Fallback to legacy format if no mentors found
                    <div className="flex">
                      <div className="relative w-10 h-10 rounded-full overflow-hidden border border-black">
                        <Image
                          src={program.mentorImage || "/placeholder-mentor.jpg"}
                          alt="Mentor"
                          fill
                          className="object-cover"
                        />
                      </div>
                      <div className="relative w-10 h-10 rounded-full overflow-hidden border-2 border-white -ml-3">
                        <div className="bg-[#012726] w-full h-full flex items-center justify-center text-white font-medium text-xs">
                          +{program.plusCount || 1}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <div>
                  <p className="text-[#002726] text-sm font-medium">
                    {program.mentorTitle || "Multiple mentors from"}
                  </p>
                  <p className="text-[#002726] text-sm">
                    {mentors.length > 0
                      ? generateMentorCompaniesText()
                      : program.mentorCompanies ||
                        program.designation ||
                        "Google, Meta and 2 more"}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Link>
    );
  }
}
